import { CpnBeneficiaryRequirementType, CpnTravelRule } from '@/types/cpn'

import { CpnPaymentMethod, CpnCountryCode } from './cpn'

type RouteKey = `${CpnCountryCode}/${CpnPaymentMethod}`

type RouteRequirements = {
  travelRule: CpnTravelRule[]
  beneficiaryAccount: CpnBeneficiaryRequirementType[]
}

const DEFAULT_TRAVEL_RULE: CpnTravelRule[] = [
  'BENEFICIARY_NAME',
  'BENEFICIARY_ADDRESS',
]

const REQUIREMENTS_BY_ROUTE: Partial<Record<RouteKey, RouteRequirements>> = {
  // BRL / PIX
  'BR/PIX': {
    travelRule: [...DEFAULT_TRAVEL_RULE],
    beneficiaryAccount: ['RECIPIENT_ID_NUMBER'],
  } satisfies RouteRequirements,

  // HKD / CHATS
  'HK/CHATS': {
    travelRule: [
      ...DEFAULT_TRAVEL_RULE,
      'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER',
    ],
    beneficiaryAccount: [
      'RECIPIENT_LEGAL_NAME',
      'RECIPIENT_CITY',
      'BANK_NAME',
      'ACCOUNT_NUMBER',
      'SWIFT_CODE',
      'BANK_CODE',
    ],
  } satisfies RouteRequirements,

  // HKD / FPS
  'HK/FPS': {
    travelRule: [
      ...DEFAULT_TRAVEL_RULE,
      'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER',
    ],
    beneficiaryAccount: [],
  } satisfies RouteRequirements,

  // HKD / WIRE
  'HK/WIRE': {
    travelRule: [
      ...DEFAULT_TRAVEL_RULE,
      'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER',
    ],
    beneficiaryAccount: [
      'RECIPIENT_LEGAL_NAME',
      'BANK_NAME',
      'BANK_COUNTRY',
      'SWIFT_CODE',
      'ACCOUNT_NUMBER',
    ],
  } satisfies RouteRequirements,

  // MXN / SPEI
  'MX/SPEI': {
    travelRule: [...DEFAULT_TRAVEL_RULE],
    beneficiaryAccount: ['CLABE'],
  } satisfies RouteRequirements,

  // CN / WIRE (destination currency USD)
  'CN/WIRE': {
    travelRule: [...DEFAULT_TRAVEL_RULE],
    beneficiaryAccount: [
      'RECIPIENT_LEGAL_NAME',
      'BANK_NAME',
      'BANK_COUNTRY',
      'SWIFT_CODE',
      'ACCOUNT_NUMBER',
    ],
  } satisfies RouteRequirements,

  // CO / BANK-TRANSFER
  'CO/BANK-TRANSFER': {
    travelRule: [
      ...DEFAULT_TRAVEL_RULE,
      'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER',
      'BENEFICIARY_PHONE_NUMBER',
    ],
    beneficiaryAccount: ['ACCOUNT_TYPE', 'ACCOUNT_NUMBER', 'BANK_NAME'],
  } satisfies RouteRequirements,

  // CO / NEQUI
  'CO/NEQUI': {
    travelRule: [
      ...DEFAULT_TRAVEL_RULE,
      'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER',
      'BENEFICIARY_PHONE_NUMBER',
      'RECIPIENT_PHONE_NUMBER',
    ],
    beneficiaryAccount: [],
  } satisfies RouteRequirements,

  // ============================================================================
  // ✅ ENABLED: IN / IMPS
  // ============================================================================
  'IN/IMPS': {
    travelRule: [
      ...DEFAULT_TRAVEL_RULE,
      'ORIGINATOR_NATIONALITY',
      'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER',
      'BENEFICIARY_DATE_OF_BIRTH',
      'BENEFICIARY_NATIONALITY',
      'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER',
      'BENEFICIARY_PHONE_NUMBER',
      'BENEFICIARY_EMAIL',
    ],
    beneficiaryAccount: ['ACCOUNT_NUMBER', 'BANK_CODE'],
  } satisfies RouteRequirements,

  // ============================================================================
  // ✅ ENABLED: NG / BANK-TRANSFER
  // ============================================================================
  'NG/BANK-TRANSFER': {
    travelRule: [
      ...DEFAULT_TRAVEL_RULE,
      'ORIGINATOR_FINANCIAL_INSTITUTION_NAME',
      'ORIGINATOR_FINANCIAL_INSTITUTION_ADDRESS',
      'ORIGINATOR_NAME',
      'ORIGINATOR_ACCOUNT_NUMBER',
      'ORIGINATOR_ADDRESS',
    ],
    beneficiaryAccount: ['RECIPIENT_LEGAL_NAME', 'ACCOUNT_NUMBER', 'BANK_NAME'],
  } satisfies RouteRequirements,

  // ============================================================================
  // 🧪 TESTING: PH / INSTAPAY
  // ============================================================================
  'PH/INSTAPAY': {
    travelRule: [
      ...DEFAULT_TRAVEL_RULE,
      'ORIGINATOR_FINANCIAL_INSTITUTION_NAME',
      'ORIGINATOR_FINANCIAL_INSTITUTION_ADDRESS',
      'ORIGINATOR_NAME',
      'ORIGINATOR_ACCOUNT_NUMBER',
      'ORIGINATOR_ADDRESS',
    ],
    beneficiaryAccount: ['RECIPIENT_LEGAL_NAME', 'ACCOUNT_NUMBER', 'BANK_NAME'],
  } satisfies RouteRequirements,

  // ============================================================================
  // 🧪 TESTING: PH / PESONET
  // ============================================================================
  'PH/PESONET': {
    travelRule: [
      ...DEFAULT_TRAVEL_RULE,
      'ORIGINATOR_FINANCIAL_INSTITUTION_NAME',
      'ORIGINATOR_FINANCIAL_INSTITUTION_ADDRESS',
      'ORIGINATOR_NAME',
      'ORIGINATOR_ACCOUNT_NUMBER',
      'ORIGINATOR_ADDRESS',
    ],
    beneficiaryAccount: ['RECIPIENT_LEGAL_NAME', 'ACCOUNT_NUMBER', 'BANK_NAME'],
  } satisfies RouteRequirements,
}

export const normalizeCountry = (country?: string) =>
  (country || '').toUpperCase()
export const normalizeMethod = (method?: string) => (method || '').toUpperCase()

export const getRequirementsForRoute = (
  country?: string,
  paymentMethod?: string
) => {
  const c = normalizeCountry(country)
  const m = normalizeMethod(paymentMethod)
  const key = `${c}/${m}` as RouteKey
  const req = REQUIREMENTS_BY_ROUTE[key]
  if (req) return req
  return {
    travelRule: DEFAULT_TRAVEL_RULE as CpnTravelRule[],
    beneficiaryAccount: [] as CpnBeneficiaryRequirementType[],
  }
}

export const getAllMetadataFieldsForRoute = (
  country?: string,
  paymentMethod?: string
) => {
  const requirements = getRequirementsForRoute(country, paymentMethod)
  return [...requirements.beneficiaryAccount, ...requirements.travelRule]
}
